import React, { forwardRef, useState } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Eye } from "@UI/Components/Icons/Eye";
import { EyeSlash } from "@UI/Components/Icons/EyeSlash";
import { Lock } from "@UI/Components/Icons/Lock";
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from "@UI/Components/Input/Input";
import useI18n from "@I18n/hooks/useI18n";

import messages from "../../messages";
import { authFieldStyles } from "../AuthFormControl/authStyles";

/** Every `Input` prop but `type`, which the toggle owns. */
export type TPasswordInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "type"
>;

/**
 * The auth step's password field, with a reveal toggle.
 *
 * `forwardRef` so react-hook-form's ref reaches the `<input>`. The toggle is
 * `tabIndex={-1}` so Tab from the field reaches Continue instead.
 */
export const PasswordInput = forwardRef<HTMLInputElement, TPasswordInputProps>(
  (props, ref) => {
    const { formatMessage } = useI18n();
    const [isRevealed, setIsRevealed] = useState(false);

    return (
      <InputGroup>
        <InputLeftElement height={authFieldStyles.height} pointerEvents="none">
          <Icon as={Lock} width="16px" height="16px" color="gray.500" />
        </InputLeftElement>
        <Input
          ref={ref}
          type={isRevealed ? "text" : "password"}
          {...authFieldStyles}
          {...props}
        />
        <InputRightElement height={authFieldStyles.height}>
          <Box
            as="button"
            type="button"
            tabIndex={-1}
            lineHeight="0"
            aria-label={formatMessage(
              isRevealed
                ? messages["taskCreation.auth.password.hide"]
                : messages["taskCreation.auth.password.show"]
            )}
            aria-pressed={isRevealed}
            onClick={() => setIsRevealed((current) => !current)}
          >
            <Icon
              as={isRevealed ? Eye : EyeSlash}
              width="16px"
              height="16px"
              color="gray.500"
            />
          </Box>
        </InputRightElement>
      </InputGroup>
    );
  }
);

PasswordInput.displayName = "PasswordInput";
